import React, { useEffect } from "react";
import {
    Row,
    Col,
    Form,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Badge,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useBizimhesapSettings } from "../hooks/useBizimhesapSettings";

const NAVY = "#0A316E";

const STATUS_META = {
    PENDING: { label: "Bekliyor", color: "#B0B7C3" },
    SENT: { label: "Gönderildi", color: "#71C30B" },
    FAILED: { label: "Başarısız", color: "#dc3545" },
    CANCELLED: { label: "İptal", color: "#E07A7A" },
};

const OPERATION_LABELS = {
    ADD_INVOICE: "Fatura",
    ADD_COLLECTION: "Tahsilat",
    CANCEL_INVOICE: "Fatura İptali",
};

const styles = `
  .bizimhesap-modern .table thead tr { background-color: #f9f9f9; }
  .bizimhesap-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
  .bizimhesap-modern .table td { vertical-align: middle; }
  .bizimhesap-modern .table-hover tbody tr:hover { background: #fcfcfd; }
  .bizimhesap-modern .btn-rounded { border-radius: 999px; }
  .bizimhesap-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
  .bizimhesap-modern .btn-navy:hover { filter: brightness(1.1); }
  .bizimhesap-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
`;

export default function BizimhesapSettingsTab({ companyId, otherActiveLabel, onSaved }) {
    const {
        formik,
        loading,
        hasSettings,
        tokenDefined,
        testing,
        testConnection,
        testResult,
        queue,
        queueLoading,
        queueStatus,
        changeQueueStatus,
        loadQueue,
        retryingId,
        retryItem,
    } = useBizimhesapSettings(companyId, onSaved);

    useEffect(() => {
        if (hasSettings) loadQueue(0);
    }, [hasSettings]);

    return (
        <div className="bizimhesap-modern">
            <style>{styles}</style>

            <p className="text-muted mb-4">
                Cari hareketleriniz (borç/fatura) Bizimhesap ön muhasebe programınıza otomatik aktarılır.
                Firm ID ve Token bilgilerinizi Bizimhesap destek ekibinden talep edebilirsiniz.
            </p>

            {!formik.values.collectionSyncEnabled && (
                <Alert color="info" className="border-0 shadow-sm">
                    <i className="bx bx-info-circle me-2"></i>
                    Tahsilat aktarımı şu an hazırlık aşamasındadır; faturalar aktarılır, tahsilat
                    kayıtları kuyrukta bekletilir ve özellik açıldığında otomatik gönderilir.
                </Alert>
            )}

            <Form onSubmit={formik.handleSubmit}>
                <Row className="g-3">
                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="bizimhesap-firmId">Firma ID (Firm ID)</Label>
                        <Input
                            id="bizimhesap-firmId"
                            name="firmId"
                            className="pill-input"
                            placeholder="Bizimhesap tarafından verilen firma ID"
                            value={formik.values.firmId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.firmId && !!formik.errors.firmId}
                        />
                        {formik.touched.firmId && formik.errors.firmId && (
                            <div className="invalid-feedback d-block">{formik.errors.firmId}</div>
                        )}
                    </Col>
                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="bizimhesap-token">
                            Token{" "}
                            {tokenDefined && (
                                <small className="text-muted fw-normal">
                                    (tanımlı — değiştirmek için yeniden girin)
                                </small>
                            )}
                        </Label>
                        <Input
                            id="bizimhesap-token"
                            name="token"
                            type="password"
                            className="pill-input"
                            placeholder={tokenDefined ? "••••••••" : "Hesabınıza ait API token"}
                            value={formik.values.token}
                            onChange={formik.handleChange}
                            autoComplete="new-password"
                        />
                    </Col>

                    <Col md={12} className="mt-3">
                        <div className="form-check form-switch">
                            <Input
                                id="bizimhesap-enabled"
                                name="enabled"
                                type="switch"
                                role="switch"
                                checked={formik.values.enabled}
                                onChange={formik.handleChange}
                            />
                            <Label className="form-check-label" htmlFor="bizimhesap-enabled">
                                Entegrasyon aktif
                            </Label>
                        </div>
                        {formik.values.enabled && otherActiveLabel && (
                            <div className="small mt-2" style={{ color: "#9a6700" }}>
                                <i className="bx bx-info-circle me-1"></i>
                                Kaydettiğinizde aktif olan <strong>{otherActiveLabel}</strong> entegrasyonu
                                otomatik olarak pasifleştirilecek.
                            </div>
                        )}
                    </Col>
                </Row>

                <div className="d-flex flex-wrap gap-2 mt-4">
                    <Button type="submit" className="btn-navy btn-rounded px-4" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : <i className="bx bx-save me-1"></i>}{" "}
                        {hasSettings ? "Güncelle" : "Kaydet"}
                    </Button>
                    {/*
                      * Buton yalnız istek uçarken devre dışı.
                      *
                      * Önceden `!hasSettings || !tokenDefined` de kapatıyordu; Bootstrap
                      * disabled butonlarda pointer-events'i kapattığı için tık da title
                      * tooltip'i de çalışmıyordu — kullanıcı basıyor, HİÇBİR ŞEY olmuyordu.
                      * Artık tıklanabiliyor ve backend'in açık mesajı ("önce token
                      * kaydedilmelidir") gerçekten görünüyor.
                      */}
                    <Button
                        type="button"
                        color="light"
                        className="btn-rounded px-4"
                        onClick={testConnection}
                        disabled={testing}
                    >
                        {testing ? <Spinner size="sm" /> : <i className="bx bx-plug me-1"></i>}{" "}
                        {testing ? "Test ediliyor..." : "Bağlantıyı Test Et"}
                    </Button>
                </div>

                {testResult && (
                    <Alert
                        color={testResult.ok ? "success" : "danger"}
                        className="border-0 shadow-sm mt-3 mb-0"
                        role="status"
                    >
                        <div className="d-flex align-items-start gap-2">
                            <i className={`bx ${testResult.ok ? "bx-check-circle" : "bx-error-circle"} fs-5`} />
                            <div>
                                <div className="fw-semibold">{testResult.message}</div>
                                {testResult.suggestion && (
                                    <div className="small mt-1">{testResult.suggestion}</div>
                                )}
                                {testResult.code && (
                                    <div className="small text-muted mt-1">Kod: {testResult.code}</div>
                                )}
                            </div>
                        </div>
                    </Alert>
                )}
            </Form>

            {hasSettings && (
                <>
                    <hr className="my-4" />

                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <h5 className="mb-0 fw-bold" style={{ color: NAVY }}>
                            <i className="bx bx-transfer-alt me-2"></i>Senkron Kuyruğu
                        </h5>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                type="select"
                                bsSize="sm"
                                className="pill-input"
                                style={{ width: 160 }}
                                value={queueStatus}
                                onChange={(e) => changeQueueStatus(e.target.value)}
                            >
                                <option value="">Tüm Durumlar</option>
                                <option value="PENDING">Bekliyor</option>
                                <option value="SENT">Gönderildi</option>
                                <option value="FAILED">Başarısız</option>
                                <option value="CANCELLED">İptal</option>
                            </Input>
                            <Button
                                color="light"
                                size="sm"
                                className="btn-rounded"
                                onClick={() => loadQueue(queue.page)}
                                disabled={queueLoading}
                            >
                                <i className="bx bx-refresh"></i>
                            </Button>
                        </div>
                    </div>

                    {queueLoading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Kuyruk yükleniyor...</div>
                        </div>
                    ) : queue.content.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            <i className="bx bx-transfer-alt fs-2 d-block mb-2"></i>
                            Senkron kaydı bulunamadı. Yeni cari hareketler oluştukça burada listelenir.
                        </div>
                    ) : (
                        <>
                            <Table className="table table-hover">
                                <Thead>
                                    <Tr>
                                        <Th>İşlem</Th>
                                        <Th>Belge No</Th>
                                        <Th>Durum</Th>
                                        <Th>Deneme</Th>
                                        <Th>Hata</Th>
                                        <Th>Tarih</Th>
                                        <Th className="text-end">Aksiyon</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {queue.content.map((item) => {
                                        const meta = STATUS_META[item.status] || STATUS_META.PENDING;
                                        return (
                                            <Tr key={item.id}>
                                                <Td>{OPERATION_LABELS[item.operation] || item.operation}</Td>
                                                <Td className="font-monospace small">
                                                    {item.documentNumber || "-"}
                                                </Td>
                                                <Td>
                                                    <Badge
                                                        pill
                                                        style={{
                                                            backgroundColor: meta.color,
                                                            borderRadius: 999,
                                                        }}
                                                    >
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.attempts}</Td>
                                                <Td
                                                    className="small text-danger"
                                                    style={{ maxWidth: 240 }}
                                                    title={item.lastError || ""}
                                                >
                                                    {item.lastError
                                                        ? item.lastError.length > 60
                                                            ? item.lastError.slice(0, 60) + "…"
                                                            : item.lastError
                                                        : "-"}
                                                </Td>
                                                <Td className="small text-muted">
                                                    {item.createdAt
                                                        ? new Date(item.createdAt).toLocaleDateString("tr-TR")
                                                        : "-"}
                                                </Td>
                                                <Td className="text-end">
                                                    {item.status === "FAILED" && (
                                                        <Button
                                                            size="sm"
                                                            className="btn-navy btn-rounded"
                                                            onClick={() => retryItem(item.id)}
                                                            disabled={retryingId === item.id}
                                                        >
                                                            {retryingId === item.id ? (
                                                                <Spinner size="sm" />
                                                            ) : (
                                                                <>
                                                                    <i className="bx bx-revision me-1"></i>
                                                                    Yeniden Dene
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>

                            {queue.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        Toplam {queue.totalElements} kayıt
                                    </small>
                                    <div className="d-flex gap-1">
                                        <Button
                                            size="sm"
                                            color="light"
                                            className="btn-rounded"
                                            disabled={queue.page === 0}
                                            onClick={() => loadQueue(queue.page - 1)}
                                        >
                                            <i className="bx bx-chevron-left"></i>
                                        </Button>
                                        <span className="align-self-center small text-muted px-2">
                                            {queue.page + 1} / {queue.totalPages}
                                        </span>
                                        <Button
                                            size="sm"
                                            color="light"
                                            className="btn-rounded"
                                            disabled={queue.page + 1 >= queue.totalPages}
                                            onClick={() => loadQueue(queue.page + 1)}
                                        >
                                            <i className="bx bx-chevron-right"></i>
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
